import React, { useState, useEffect } from 'react';
import Barcode from 'react-barcode';
import { LoaderSpinner } from '../LoaderSpinner';
import { StatusSelect } from '../StatusSelect';
import ProductBillOfMaterials from './ProductBillOfMaterials';
import { printProductBarcode, ProductLoadingKeys, updateAmazonByProduct, updateBarcodeByProduct, updateBOMVariantByProduct, updateBOMVariantsByProduct, updateInactiveSkuByProduct, updateNameByProduct, updateReplenishableByProduct, updateSkuByProduct, updateSourceSkuByProduct, updateStatusByProduct, updateVendorByProduct } from '../../slices/productsSlice';
import { getSuppliers, ProductGroupLoadingKeys, updateSingleProductInGroup } from '../../slices/productGroupSlice';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import printJS from 'print-js';
import ProductMaterialSpecifications from './ProductMaterialSpecifications';
import VariantModal from './VariantModal';
import { SourceSKU } from '../common/SourceSKU';
import { IsReplanishable } from '../common/IsReplanishable';
import { IsAmazon } from '../common/IsAmazon';
import { ConnectVariantButton } from '../common/ConnectVariantButton';
import MOQ from './MOQ';
import { arrayHasItems } from '../../helpers/common';
import { BOMVariant } from './BOMVariant';
import { ConfrimModal } from '../ConfrimModal';
import { SelectDropdown } from '../SelectDropdown';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ACTIVE } from '../../consts/inventoryStatus';

const ProductVariantSidebar = ({ product, selectedTab, onChangeSelectedTab, access }) => {
  const [activeTab, setActiveTab] = useState(selectedTab); 
  const [barcodeValue, setBarcodeValue] = useState(null);
  const [nameValue, setNameValue] = useState(null);
  const [skuValue, setSkuValue] = useState(null);
  const [inactiveSkuValue, setInactiveSkuValue] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [colorValue, setColorValue] = useState('');
  const [leadTimeValue, setLeadTimeValue] = useState(null);
  const barcodeRef = React.useRef(null);
  const [variantModalData, setVariantModalData] = useState(null);
  const [openedEditSaveByBOM, setOpenedEditSaveByBOM] = useState(false);

  const dispatch = useDispatch();
  const {
      loading: groupLoading,
      loadingKeys: groupLoadingKeys,
      vendorsToSelect,
  } = useSelector((state) => state.productGroup);
  const {
    loading: productLoading,
    loadingKeys: productLoadingKeys
  } = useSelector((state) => state.products);

  useEffect(() => {
    dispatch(getSuppliers({}))
  }, []);
  useEffect(() => {
    // Initialize state values from product data
    setNameValue(product.name || null);
    setSkuValue(product.sku || null);
    setInactiveSkuValue(product.inactive_skus || null);
    setColorValue(product.color || '');
    setBarcodeValue(product.barcode || null);
  }, [product]);

  const handlePrintBarcode = async () => {
    const resultAction = await dispatch(printProductBarcode({
      productData: {
        productName: product.name,
        sku: product.sku,
        barcode: product.barcode,
        barcodeUrl: product.barcodeUrl
      }
    }));
    const res = unwrapResult(resultAction);
    const newTab = window.open(res.url, '_blank');
    if (newTab) {
        newTab.focus();
    }
    // printJS({
    //   printable: res.url,
    //   type: 'pdf' ,
    //   multiple: true
    // });
  }

  useEffect(() => {
    setSelectedSupplier(product?.vendor_id)
  }, [product?.vendor_id])

  const handleStatusChange = async (productId, newStatus) => {
    if (access === "edit") {
      await dispatch(updateStatusByProduct({
        productId, 
        newStatus,
        requestToUpdate: updateSingleProductInGroup,
      }))
    }
  };

  const handleIsAmazonChange = async (productId, amazonValue) => {
    if (access === "edit") {
      await dispatch(updateAmazonByProduct({
        productId,
        isAmazonManually: amazonValue || false,
        requestToUpdate: updateSingleProductInGroup,
      }))
    }
  };

  const handleIsReplanishableChange = async (productId, replenishableValue) => {
    if (access === "edit") {
      await dispatch(updateReplenishableByProduct({
        productId,
        isReplenishable: replenishableValue || false,
        requestToUpdate: updateSingleProductInGroup,
      }));
    }
  };

  const handleAddVariant = async (variant) => {
    if (access === "edit") {
      try {
        await dispatch(updateSourceSkuByProduct({
          productId: product.id,
          sourceSku: variant.id,
          requestToUpdate: updateSingleProductInGroup,
          type: 'add'
        }));
        setVariantModalData(null);
      } catch (error) {
        console.error("Error updating source SKU:", error);
      }
    }
  }

  const handleRemoveBOMVariant = async (sku) => {
    if (access === "edit") {
      await dispatch(updateBOMVariantByProduct({
        productId: product.id,
        bomProductSku: sku,
        requestToUpdate: updateSingleProductInGroup,
        type: 'remove'
      }));
    }
  };

  const handleAddBOMVariant = async (variant) => {
    if (access === "edit") {
      await dispatch(updateBOMVariantByProduct({
        productId: product.id,
        bomProductSku: variant?.id,
        requestToUpdate: updateSingleProductInGroup,
        type: 'add'
      }));
      setVariantModalData(null);
    }
  }

  const handleAcceptBOMVariants = async () => {
    if (access === "edit") {
      await dispatch(updateBOMVariantsByProduct({
        productId: product.id,
        bomProductSkus: (product.parentBomSkus || []).map((product) => product?.id),
        requestToUpdate: updateSingleProductInGroup
      }));
    }
  };

  const getSuppliersToSelectIsLoading = () =>
    groupLoading && groupLoadingKeys.includes(ProductGroupLoadingKeys.getSuppliers);
  const barcodeDownloadIsLoading = () =>
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.generateBarcode);
  const productNameIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateName);
  const productSkuIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateSku);
  const productBarcodeIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateBarcode);
  const productInactiveSkuIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateInactiveSku);
  const productAmazonIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateAmazon);
  const productStatusIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateStatus);
  const productReplanishableIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateReplanishable);
  const productSourceSkuIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateSourceSku);
  const updateBOMVariantIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateBOMVariant);
  const updateBOMVariantsIsLoading = () => 
    productLoading && productLoadingKeys.includes(ProductLoadingKeys.updateBOMVariants)

  return (
    <div>
        <div className="flex justify-between items-start mb-4">
          <p className="text-sm text-gray-500">
            SKU: {product.sku}
          </p>
        </div>
        
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex -mb-px">
            <button
              onClick={() => {
                setActiveTab('details');
                onChangeSelectedTab?.('variant', 'details');
              }}
              className={`py-2 px-2 text-sm font-medium ${
                activeTab === 'details'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } focus:outline-none`}
              aria-label="Show variant details"
            >
              Details
            </button>
            <button
              onClick={() => {
                setActiveTab('bom');
                onChangeSelectedTab?.('variant', 'bom');
              }}
              className={`ml-4 py-2 px-2 text-sm font-medium ${
                activeTab === 'bom'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } focus:outline-none`}
              aria-label="Show bill of materials"
            >
              BOM
            </button>
          </nav>
        </div>
        
        {/* Tab Content */}
        {activeTab === 'details' && (
          <div key={product}>
            {/* Product Image and Barcode - Side by Side */}
            <div className="flex flex-row gap-4 mb-4">
              {/* Product Image */}
              <div className="w-1/2">
                {product.image ? (
                  <div className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm p-4 flex items-center justify-center h-full">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="max-w-full max-h-48 object-contain min-h-48"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://placehold.co/300x200/e6e6e6/cccccc?text=No+Image';
                      }}
                    />
                  </div>
                ) : (
                  <div className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm p-4 flex items-center justify-center h-full">
                    <svg className="w-16 min-h-48 max-h-48 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
              
              {/* Barcode Display and Download Section */}
              <div className="w-1/2">
                {product.barcode ? (
                  <div className="border border-gray-200 rounded-md p-4 bg-gray-50 h-full flex flex-col">
                    <div className="flex justify-end items-center mb-4">
                      <div className="relative">
                        <div className='flex items-center gap-2'>
                          {barcodeDownloadIsLoading() && (
                            <LoaderSpinner height='20' width='20' colorSpinner='black' border='3' />
                          )}
                          <button
                            onClick={async () => await handlePrintBarcode()}
                            disabled={barcodeDownloadIsLoading()}
                            className="flex whitespace-nowrap w-fit disabled:bg-gray-200 disabled:text-black items-center px-2.5 py-1.5 text-xs font-medium rounded text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            aria-label="Download barcode as PDF"
                          >
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center mb-6">
                      <article className="bg-white max-w-[400px] w-full">
                        <div className="relative">
                          <h6 className="text-center text-[0.8vw] 2xl:text-2xl !leading-none relative z-10 absolute -bottom-0.5 md:-bottom-1 2xl:-bottom-4">{product.sku}</h6>
                        </div>
                        <div ref={barcodeRef} className="flex justify-center flex-grow h-full bg-white ">
                          <Barcode
                            value={product.barcode}
                            width={3}
                            format="CODE128"
                            displayValue={true}
                            fontOptions=""
                            font="monospace"
                            textAlign="center"
                            textPosition="bottom"
                            textMargin={0}
                            fontSize={20}
                            background="#ffffff"
                            lineColor="#000000"
                            className="h-full"
                          />
                        </div>
                      </article>
                    </div>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-md p-4 bg-gray-50 h-full flex items-center justify-center">
                    <p className="text-sm text-gray-500">No barcode available</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="border-t border-gray-200 pt-4 mt-2">
              <dl className="divide-y divide-gray-200">
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="text-sm text-gray-900">
                    <div className="relative">
                      <div className='flex items-center gap-2'>
                        {productNameIsLoading() && (<LoaderSpinner height='20' width='20' colorSpinner='black' border='3' />)}
                        {/* <input
                          key={product.name}
                          onBlur={async (e) => {
                            if (e.target.value === product.name) return;
                            const editedValue = e.target.value.trim();
                            await dispatch(updateNameByProduct({
                              productId: product.id,
                              name: editedValue,
                              requestToUpdate: updateSingleProductInGroup
                            }))
                            setSkuValue(null);
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                                if (e.target.value === product.name) return;
                                e.preventDefault();
                                const editedValue = e.target.value.trim();
                                await dispatch(updateNameByProduct({
                                  productId: product.id,
                                  name: editedValue,
                                  requestToUpdate: updateSingleProductInGroup
                                }))
                                setSkuValue(null);
                            }
                          }}
                          disabled={productNameIsLoading()}
                          onChange={(e) => setNameValue(e.target.value)}
                          // className="w-40 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200"
                          readOnly
                          className="w-40 bg-white outline-none"
                        /> */}
                        <span className="w-52 bg-white outline-none text-right">{nameValue !== null ? nameValue : product.name}</span>
                      </div>
                    </div>
                  </dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">SKU</dt>
                  <dd className="text-sm text-gray-900">
                    <div className="relative">
                      <div className='flex items-center gap-2'>
                        {productSkuIsLoading() && (<LoaderSpinner height='20' width='20' colorSpinner='black' border='3' />)}
                        {/* <input
                          key={product.sku}
                          value={skuValue !== null ? skuValue : product.sku}
                          onBlur={async (e) => {
                            if (e.target.value === product.sku) return;
                            const editedValue = e.target.value.trim();
                            await dispatch(updateSkuByProduct({
                              productId: product.id,
                              sku: editedValue,
                              requestToUpdate: updateSingleProductInGroup
                            }))
                            setSkuValue(null);
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                                if (e.target.value === product.sku) return;
                                e.preventDefault();
                                const editedValue = e.target.value.trim();
                                await dispatch(updateSkuByProduct({
                                  productId: product.id,
                                  sku: editedValue,
                                  requestToUpdate: updateSingleProductInGroup
                                }))
                                setSkuValue(null);
                            }
                          }}
                          disabled={productSkuIsLoading()}
                          onChange={(e) => setSkuValue(e.target.value)}
                          // className="w-40 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200"
                          readOnly
                          className="w-40 bg-white outline-none"
                        /> */}
                        <span className="w-52 bg-white outline-none text-right">{skuValue !== null ? skuValue : product.sku}</span>
                      </div>
                    </div>
                  </dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Inactive SKU</dt>
                  <dd className="text-sm text-gray-900">
                    <div className="relative">
                      <div className='flex items-center gap-2'>
                        {productInactiveSkuIsLoading() && (<LoaderSpinner height='20' width='20' colorSpinner='black' border='3' />)}
                        <input
                          key={product.inactive_skus}
                          value={inactiveSkuValue !== null ? inactiveSkuValue : product.inactive_skus}
                          inputMode="text"
                          onBlur={async (e) => {
                            if (e.target.value === product.inactive_skus) return;
                            const editedValue = e.target.value.trim();
                            await dispatch(updateInactiveSkuByProduct({
                              productId: product.id,
                              inactiveSku: editedValue || "",
                              requestToUpdate: updateSingleProductInGroup
                            }))
                            setInactiveSkuValue(null);
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                                if (e.target.value === product.inactive_skus) return;
                                e.preventDefault();
                                const editedValue = e.target.value.trim();
                                await dispatch(updateInactiveSkuByProduct({
                                    productId: product.id,
                                    inactiveSku: editedValue || "",
                                    requestToUpdate: updateSingleProductInGroup
                                }))
                                setInactiveSkuValue(null);
                            }
                          }}
                          disabled={productInactiveSkuIsLoading()}
                          onChange={(e) => {
                            setInactiveSkuValue(e.target.value);
                          }}
                          className="w-52 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200"
                          // readOnly
                          // className="w-40 bg-white outline-none"
                        />
                        {/* <span className="w-52 bg-white outline-none text-right">{inactiveSkuValue !== null ? inactiveSkuValue : product.inactive_skus}</span> */}
                      </div>
                    </div>
                  </dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Barcode</dt>
                  <dd className="text-sm text-gray-900">
                    <div className="relative">
                      <div className='flex items-center gap-2'>
                        {productBarcodeIsLoading() && (<LoaderSpinner height='20' width='20' colorSpinner='black' border='3' />)}
                        {/* <input
                          key={product.barcode}
                          value={barcodeValue !== null ? barcodeValue : product.barcode}
                          onBlur={async (e) => {
                            if (e.target.value === product.barcode) return;
                            const editedValue = e.target.value.trim();
                            await dispatch(updateBarcodeByProduct({
                              productId: product.id, 
                              barcode: editedValue,
                              requestToUpdate: updateSingleProductInGroup
                            }))
                            setBarcodeValue(null);
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                                if (e.target.value === product.barcode) return;
                                e.preventDefault();
                                const editedValue = e.target.value.trim();
                                await dispatch(updateBarcodeByProduct({
                                  productId: product.id, 
                                  barcode: editedValue,
                                  requestToUpdate: updateSingleProductInGroup
                                }))
                                setBarcodeValue(null);
                            }
                          }}
                          disabled={productBarcodeIsLoading()}
                          onChange={(e) => {  
                            setBarcodeValue(e.target.value)
                          }}
                          // className={`w-40 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200`}
                          readOnly
                          className="w-40 bg-white outline-none"
                        /> */}
                        <span className="w-52 bg-white outline-none text-right">{barcodeValue !== null ? barcodeValue : product.barcode}</span>
                      </div>
                    </div>
                  </dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="text-sm text-gray-900">
                    <div className="relative">
                      <div className='flex items-center gap-2'>
                        {productStatusIsLoading() && (<LoaderSpinner height='20' width='20' colorSpinner='black' border='3' />)}
                        <div className="w-24">
                          <StatusSelect
                            key={product.status}
                            idItem={product.id}
                            access={access}
                            currentStatus={product.status}
                            handleStatusChange={handleStatusChange}
                            isDisabled={productStatusIsLoading()}
                          />
                        </div>
                      </div>
                    </div>
                  </dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Is Amazon</dt>
                  <dd className="text-sm text-gray-900">
                    <IsAmazon
                      key={`${product?.isAmazon}_${product.isAmazonManually}`}
                      product={product}
                      access={access}
                      isLoading={productAmazonIsLoading()}
                      groupUpdate={updateSingleProductInGroup}
                    />
                  </dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Is Replenishable</dt>
                  <dd className="text-sm text-gray-900">
                    <IsReplanishable
                      key={product?.isReplenishable}
                      product={product}
                      disabled={product.status !== ACTIVE}
                      access={access}
                      isLoading={productReplanishableIsLoading()}
                      groupUpdate={updateSingleProductInGroup}
                    />
                  </dd>
                </div>
                {!product.isReplenishable && (
                  <div className="py-3 flex justify-between gap-5 items-center">
                    <dt className="text-sm font-medium text-gray-500 whitespace-nowrap">Source SKU</dt>
                    <dd className="text-sm text-gray-900">
                      <SourceSKU
                        key={product?.sourceSkus?.length}
                        isLoading={productSourceSkuIsLoading()}
                        product={product}
                        access={access}
                        removeCallback={updateSingleProductInGroup}
                        openModalCallback={() => setVariantModalData({
                          type: 'source-sku',
                          addVariantFunc: (variant) => handleAddVariant(variant),
                          options: {
                            isReplenishable: true,
                            defaultSearchTerm: product.barcode || ''
                          }
                        })}
                      />
                    </dd>
                  </div>
                )}
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Supplier</dt>
                  <dd className="text-sm text-gray-900">
                      <div className='flex flex-row gap-2 items-center'>
                          {selectedSupplier && access === "edit" && (
                            <XMarkIcon className='h-5 w-5 cursor-pointer' onClick={async () => {
                              await dispatch(updateVendorByProduct({
                                productId: product.id, 
                                newVendor: null,
                                requestToUpdate: updateSingleProductInGroup,
                              }))
                              setSelectedSupplier(null);
                            }}/>
                          )}
                          <div className='w-52'>
                            <SelectDropdown
                              key={selectedSupplier || "empty"}
                              disabled={getSuppliersToSelectIsLoading() || access !== "edit"}
                              options={vendorsToSelect}
                              value={selectedSupplier}
                              onChange={async (value) => {
                                if (access === "edit") {
                                  if (value !== selectedSupplier) {
                                    await dispatch(updateVendorByProduct({
                                      productId: product.id, 
                                      newVendor: value,
                                      requestToUpdate: updateSingleProductInGroup,
                                    }))
                                  }
                                  setSelectedSupplier(value); 
                                }
                              }}
                              selectClassname='!bg-white !px-2 !py-1 !text-sm !rounded'
                              selectedValueClassname='truncate'
                              itemValueClassname='!text-sm'
                            />
                          </div>
                        </div>
                      </dd>
                    </div>
                {/* <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Color</dt>
                  <dd className="text-sm text-gray-900">
                    <input
                      key={product.color}
                      value={colorValue || product.color}
                      onChange={(e) => setColorValue(e.target.value)}
                      className="w-40 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2"
                    />
                  </dd>
                </div> */}
                {/* <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Lead Time</dt>
                  <dd className="text-sm text-gray-900" key={product.lead_time}>
                    <div className="relative">
                      <div className='flex items-center gap-2'>
                        <input
                          key={product.lead_time}
                          value={leadTimeValue === null ? product.lead_time : leadTimeValue}
                          onBlur={async (e) => {
                            if (parseInt(e.target.value) === product.lead_time) return
                            if (parseInt(e.target.value || 0) >= 0) {
                              // Implementation for saving lead time
                              console.log('Save lead time:', e.target.value);
                              setLeadTimeValue(null);
                            }
                          }}
                          onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                              if (parseInt(e.target.value) === product.lead_time) return
                              e.preventDefault();
                              if (parseInt(e.target.value || 0) >= 0) {
                                // Implementation for saving lead time
                                console.log('Save lead time:', e.target.value);
                                setLeadTimeValue(null);
                              }
                            }
                          }}
                          onChange={(e) => {  
                            let newValue = e.target.value;
                            // Only allow numbers and minus sign at the start
                            if (/^\d*$/.test(newValue)) {
                              if (newValue.startsWith('0') && newValue.length > 1) {
                                newValue = newValue.replace(/^0+/, '');
                              }
                              setLeadTimeValue(newValue);
                            }
                          }}
                          className={`w-40 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200`}
                        />
                      </div>
                    </div>
                  </dd>
                </div> */}
              </dl>
            </div>
            
            {/* {product.description && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                <p className="text-sm text-gray-700">
                  {product.description}
                </p>
              </div>
            )} */}
          </div>
        )}
        
        {/* BOM Tab Content */}
        {activeTab === 'bom' && (
          <section key={product}>
            {/* <ProductBillOfMaterials
              product={product}
              access={access}
              type={"Product"}
            /> */}
            <BOMVariant
              key={product?.bomSkus?.length}
              skus={product?.bomSkus}
              access={access}
              parentBomSkus={product?.parentBomSkus || null}
              onClickVariantCallback={() => setVariantModalData({
                type: 'variant-bom-sku',
                addVariantFunc: (variant) => handleAddBOMVariant(variant),
                options: {}
              })}
              onClickEdit={() => setOpenedEditSaveByBOM(true)}
              onClickRemoveCallback={(bomVariantSku) => handleRemoveBOMVariant(bomVariantSku)}
              isDisabled={updateBOMVariantIsLoading()}
              isLoading={updateBOMVariantIsLoading()}
            />
            <MOQ
              product={product}
              access={access}
              type={"Product"}
            />
            <ProductMaterialSpecifications
              access={access}
              product={product}
            />
          </section>
        )}
        {variantModalData && (
          <VariantModal
            isOpen={variantModalData}
            onClose={() => {
              setVariantModalData(null);
            }}
            onAddVariant={(data) => variantModalData?.addVariantFunc?.(data)}
            {...variantModalData?.options}
          />
        )}
        <ConfrimModal
          isOpen={openedEditSaveByBOM}
          title={`Are you sure want to accept sku's ${(product?.parentBomSkus || []).map((parentBom) => parentBom?.sku).join(", ")} for this product?`}
          isLoading={updateBOMVariantsIsLoading()}
          size="max-w-xl"
          confirmButtonTitle={"Save"}
          submitClick={async () => {
            setOpenedEditSaveByBOM(false);
            await handleAcceptBOMVariants();
          }}
          cancelClick={() => {
            setOpenedEditSaveByBOM(false);
          }}
        />
    </div>
  );
};

export default ProductVariantSidebar; 